import Phaser from 'phaser';
import { CampaignState } from '../state/CampaignState';
import { getTileMovementMultiplier } from '../generation/TilesetLoader';
import { getTilesetConfig } from '../scenes/PreloadScene';

const MAX_HEALTH = 3;
const HEALTH_BAR_WIDTH = 24;
const HEALTH_BAR_HEIGHT = 3;
const HEALTH_BAR_OFFSET_Y = -18;
const SELECTION_RING_RADIUS = 14;

export type WeaponType = 'machinegun' | 'grenade' | 'rocket';

export interface WeaponInventory {
  currentWeapon: WeaponType;
  grenades: number;
  rockets: number;
  grenadeCooldownUntil: number;
  rocketCooldownUntil: number;
}

export enum SoldierState {
  Healthy,
  Wounded,
  Dying,
  Dead,
}

export class Soldier extends Phaser.Physics.Matter.Sprite {
  health: number = MAX_HEALTH;
  state: SoldierState = SoldierState.Healthy;
  currentPath: { x: number; y: number }[] = [];
  isMoving: boolean = false;
  soldierName: string = '';
  exactDestination?: { x: number; y: number };

  private readonly woundedSpeedMultiplier = 2.0;

  weapons: WeaponInventory = {
    currentWeapon: 'machinegun',
    grenades: 3,
    rockets: 2,
    grenadeCooldownUntil: 0,
    rocketCooldownUntil: 0,
  };

  /** Set by GameScene after spawn to enable terrain-based movement speed. */
  groundLayer?: Phaser.Tilemaps.TilemapLayer;

  private _isSelected: boolean = false;

  // Visual components
  private selectionRing: Phaser.GameObjects.Graphics;
  private healthBarBg: Phaser.GameObjects.Rectangle;
  private healthBarFill: Phaser.GameObjects.Rectangle;
  private skullMarker: Phaser.GameObjects.Text | null = null;
  private graveMarker: Phaser.GameObjects.Graphics | null = null;

  constructor(scene: Phaser.Scene, x: number, y: number, name: string = '') {
    super(scene.matter.world, x, y, 'soldier');
    this.soldierName = name;
    scene.add.existing(this);
    this.setDepth(10);
    this.play('soldier-idle');

    // Circular Matter body for soldier-to-soldier collision
    // Radius 8 px — small enough to avoid corner-clipping on 32 px tiles while
    // still providing visible soldier-to-soldier and soldier-to-wall separation.
    this.setBody({ type: 'circle', radius: 8 }, { isStatic: false, frictionAir: 0.2, restitution: 0 });
    this.setCollisionCategory(1);
    this.setCollidesWith([1, 2]); // soldiers collide with each other (1) and walls (2)

    // Selection ring (hidden by default)
    this.selectionRing = scene.add.graphics().setDepth(9);
    this.updateSelectionRing();

    // Health bar background
    this.healthBarBg = scene.add
      .rectangle(x, y + HEALTH_BAR_OFFSET_Y, HEALTH_BAR_WIDTH, HEALTH_BAR_HEIGHT, 0x440000)
      .setDepth(20);

    // Health bar fill
    this.healthBarFill = scene.add
      .rectangle(x, y + HEALTH_BAR_OFFSET_Y, HEALTH_BAR_WIDTH, HEALTH_BAR_HEIGHT, 0x00cc44)
      .setDepth(21)
      .setOrigin(0, 0.5);

    this.syncHealthBarPosition();
  }

  get isSelected(): boolean {
    return this._isSelected;
  }

  setSelected(value: boolean): void {
    this._isSelected = value;
    this.updateSelectionRing();
  }

  private updateSelectionRing(): void {
    this.selectionRing.clear();
    if (this._isSelected) {
      this.selectionRing.lineStyle(2, 0x00ff88, 0.9);
      this.selectionRing.strokeCircle(this.x, this.y, SELECTION_RING_RADIUS);
    }
  }

  private syncHealthBarPosition(): void {
    const bx = this.x - HEALTH_BAR_WIDTH / 2;
    this.healthBarBg.setPosition(this.x, this.y + HEALTH_BAR_OFFSET_Y);
    this.healthBarFill.setPosition(bx, this.y + HEALTH_BAR_OFFSET_Y);
    const ratio = Math.max(0, this.health / MAX_HEALTH);
    this.healthBarFill.setSize(HEALTH_BAR_WIDTH * ratio, HEALTH_BAR_HEIGHT);
  }

  private syncSelectionRingPosition(): void {
    this.selectionRing.clear();
    if (this._isSelected) {
      this.selectionRing.lineStyle(2, 0x00ff88, 0.9);
      this.selectionRing.strokeCircle(this.x, this.y, SELECTION_RING_RADIUS);
    }
  }

  syncVisuals(): void {
    this.syncHealthBarPosition();
    this.syncSelectionRingPosition();
  }

  /** Legacy compatibility – used when there is only one soldier */
  select(): void {
    this.setSelected(true);
  }

  deselect(): void {
    this.setSelected(false);
  }

  move(path: { x: number; y: number }[], exactDestination?: { x: number; y: number }): void {
    if (!path || path.length === 0) return;
    if (this.state === SoldierState.Dying || this.state === SoldierState.Dead) return;
    this.currentPath = [...path];
    this.exactDestination = exactDestination;
    this.isMoving = true;
    this.play('soldier-walk', true);
    this.spawnDustCloud();
    // velocity steering runs in update()
  }

  /** Spawn small beige dust particles at feet */
  private spawnDustCloud(): void {
    const dustColors = [0xd4b896, 0xc8aa80, 0xe0c9a0];
    const count = Phaser.Math.Between(3, 5);
    for (let i = 0; i < count; i++) {
      const offsetX = Phaser.Math.FloatBetween(-8, 8);
      const size = Phaser.Math.FloatBetween(2, 5);
      const color = dustColors[Phaser.Math.Between(0, dustColors.length - 1)];

      const dust = this.scene.add
        .circle(this.x + offsetX, this.y + 12, size, color, 0.45)
        .setDepth(5);

      this.scene.tweens.add({
        targets: dust,
        x: dust.x + Phaser.Math.FloatBetween(-12, 12),
        y: dust.y + Phaser.Math.FloatBetween(-8, -16),
        alpha: 0,
        scaleX: 1.5,
        scaleY: 1.5,
        duration: 300,
        ease: 'Power1',
        delay: Phaser.Math.Between(0, 80),
        onComplete: () => dust.destroy(),
      });
    }
  }

  isDead(): boolean {
    return this.state === SoldierState.Dead;
  }

  takeDamage(amount: number): void {
    if (this.isDead()) return;
    this.health -= amount;
    if (this.health < 0) this.health = 0;

    this.syncHealthBarPosition();

    // Update state based on HP thresholds (state only goes in one direction)
    if (this.health <= 0 && this.state !== SoldierState.Dead) {
      this.state = SoldierState.Dead;
      this.onDeath();
      return;
    } else if (this.health <= Math.floor(MAX_HEALTH / 3) && this.state < SoldierState.Dying) {
      this.state = SoldierState.Dying;
      this.currentPath = [];
      this.isMoving = false;
      this.play('soldier-idle', true);
      this.scene.time.delayedCall(3000, () => {
        this.state = SoldierState.Dead;
        this.onDeath();
      });
      return;
    } else if (this.health <= Math.floor((MAX_HEALTH * 2) / 3) && this.state < SoldierState.Wounded) {
      this.state = SoldierState.Wounded;
      // Red tint as placeholder for wounded art
      this.setTint(0xff4444);
      return;
    }

    // Flash red on hit (only when Healthy)
    if (this.state === SoldierState.Healthy) {
      this.setTint(0xff0000);
      this.scene.time.delayedCall(150, () => {
        if (this.state === SoldierState.Healthy) this.clearTint();
      });
    }
  }

  private onDeath(): void {
    // Register death in campaign state
    if (this.soldierName) {
      CampaignState.killSoldier(this.soldierName);
    }

    // Notify scene for audio / VFX
    this.scene.events.emit('soldierDied', this.x, this.y);

    // Hide health bar and selection ring
    this.healthBarBg.setVisible(false);
    this.healthBarFill.setVisible(false);
    this.selectionRing.setVisible(false);

    // Leave body on screen: tint grey and drop to ground depth
    this.setTint(0x666666);
    this.setAlpha(0.7);
    this.setDepth(2);

    // Stop physics body (Matter.js: set static so it stops being pushed)
    this.setStatic(true);
    this.setVelocity(0, 0);

    // Show name label above body
    if (this.soldierName) {
      this.scene.add
        .text(this.x, this.y - 24, this.soldierName, {
          fontSize: '9px',
          color: '#888888',
          fontFamily: 'monospace',
        })
        .setOrigin(0.5)
        .setDepth(6);
    }
  }

  selectWeapon(type: WeaponType): void {
    this.weapons.currentWeapon = type;
  }

  getWeaponLabel(): string {
    switch (this.weapons.currentWeapon) {
      case 'grenade': return `GRN x${this.weapons.grenades}`;
      case 'rocket':  return `RKT x${this.weapons.rockets}`;
      default:        return 'MG';
    }
  }

  update(_delta: number): void {
    if (!this.isMoving || this.currentPath.length === 0) {
      this.setVelocity(0, 0);
      return;
    }

    const SPEED = 3; // Matter units per frame (pixels at physics step rate)
    const ARRIVAL_RADIUS = 6;

    // Apply terrain speed multiplier
    const tile = this.groundLayer?.getTileAtWorldXY(this.x, this.y);
    const tilesetConfig = getTilesetConfig();
    const terrainMult = (tile && tilesetConfig) ? getTileMovementMultiplier(tilesetConfig, tile.index) : 1.0;
    const stateMult = this.state === SoldierState.Wounded ? this.woundedSpeedMultiplier : 1.0;
    const speed = SPEED / (terrainMult * stateMult);

    const isFinal = this.currentPath.length === 1;
    const target = (isFinal && this.exactDestination) ? this.exactDestination : this.currentPath[0];

    const dx = target.x - this.x;
    const dy = target.y - this.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist <= ARRIVAL_RADIUS) {
      this.setPosition(target.x, target.y);
      this.currentPath.shift();
      if (this.currentPath.length === 0) {
        this.isMoving = false;
        this.exactDestination = undefined;
        this.play('soldier-idle', true);
        this.setVelocity(0, 0);
      }
    } else {
      const vx = (dx / dist) * speed;
      const vy = (dy / dist) * speed;
      this.setVelocity(vx, vy);

      // Facing direction
      const angle = Math.atan2(dy, dx);
      const DIRECTIONS = ['e', 'se', 's', 'sw', 'w', 'nw', 'n', 'ne'];
      const dirIndex = Math.round(((angle + Math.PI) / (Math.PI * 2)) * 8) % 8;
      const animKey = `soldier-walk-${DIRECTIONS[dirIndex]}`;
      if (this.anims.exists(animKey)) {
        if (this.anims.currentAnim?.key !== animKey) this.play(animKey, true);
        this.setRotation(0);
      } else {
        if (this.anims.currentAnim?.key !== 'soldier-walk') this.play('soldier-walk', true);
        this.setRotation(angle + Math.PI / 2);
      }
    }

    this.syncVisuals();
  }

  destroy(fromScene?: boolean): void {
    this.selectionRing.destroy();
    this.healthBarBg.destroy();
    this.healthBarFill.destroy();
    if (this.skullMarker) this.skullMarker.destroy();
    if (this.graveMarker) this.graveMarker.destroy();
    super.destroy(fromScene);
  }
}
